import React, { useMemo } from 'react';
import { Table } from 'syngenta-digital-cropwise-react-ui-kit';
import getLinkedRecordsColumns from '../../columns';
import { LINKED_RECORDS_PAGE_SIZE } from '../../constants';
import { LinkedRecordRow } from '../../types';

/**
 * Module scope, not inline in the pagination config: a component declared
 * inside another is a fresh type each render, so React remounts it rather than
 * updating it.
 */
const renderPaginationTotal = (total: number, range: [number, number]) => (
  <span className="text-neutral-60">
    {range[0]}-{range[1]} of {total}
  </span>
);

interface LinkedRecordsTableProps {
  records: LinkedRecordRow[];
  totalRecords: number;
  page: number;
  onPageChange: (page: number) => void;
  onViewMore: (record: LinkedRecordRow) => void;
}

const LinkedRecordsTable = ({
  records,
  totalRecords,
  page,
  onPageChange,
  onViewMore
}: LinkedRecordsTableProps) => {
  const columns = useMemo(() => getLinkedRecordsColumns(onViewMore), [
    onViewMore
  ]);

  const hasPaginationNavigation = totalRecords > LINKED_RECORDS_PAGE_SIZE;

  const pagination = useMemo(
    () => ({
      current: page + 1,
      pageSize: LINKED_RECORDS_PAGE_SIZE,
      total: totalRecords,
      showSizeChanger: false,
      position: ['bottomRight'],
      showTotal: renderPaginationTotal,
      onChange: (nextPage: number) => onPageChange(nextPage - 1)
    }),
    [page, totalRecords, onPageChange]
  );

  return (
    <div data-testid="linked-records-table">
      <div
        className={`application-view-products-tab-table ${
          hasPaginationNavigation
            ? ''
            : 'application-view-products-tab-table-no-pagination'
        }`}
      >
        {/*
          No `scroll` config: this section sits in a long scrolling page, so the
          full page of rows renders and the page itself scrolls. A viewport-derived
          `scroll.y` — correct inside the fixed-height operations detail panel —
          would clamp the body to a few hundred pixels here and hide most of the
          page behind an inner scrollbar. Leaving `scroll.x` unset also keeps antd
          fitting the columns to the container, so it never scrolls sideways.
        */}
        <Table
          columns={columns}
          pagination={pagination as any}
          dataSource={records}
          rowKey="id"
        />
      </div>
    </div>
  );
};

export default LinkedRecordsTable;
